import { FC, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'

import statisticContext from 'context/statisticsContext'
import Commentaries from 'components/parts/commentaries/commentaries'
import WriteUs from 'components/parts/write-us/write-us-popup/write-us'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import BreadcrumbsPage from 'components/parts/breadcrumb-page/breadcrumb-page'
import Playlist from '../article-videoproject/playlist/playlist'
import Loader from 'components/parts/loader/loader'
import VideoFrameMaterial from 'components/webinar/video-block/video-frame-material'
import VideoBlockInfo from 'components/webinar/video-block/video-block-info'
import SwitchToggle from 'components/parts/switch-toggle/switch-toggle'
import usePCWindow from 'hooks/usePCWindow'
import useLectorsSubscription from 'hooks/useLectorsSubscription'

import { ScreenSize } from 'constants/media-constanst'
import { URL_SITE } from 'constants/data'
import { IMaterialCard } from 'constants/types/card.types'
import { IMaterial } from 'constants/types/webinar.types'
import { SocketEvents } from 'constants/events'
import { PLACEFLAG_PAST, PLACEFLAG_VIDEO } from 'constants/analytic-data'
import { getPlayerMethods } from 'components/webinar/video-block/utils'
import { getCurrVideoIndex } from '../article-videoproject/utils'
import { OPTIONS, SWITCH_DESCRIPTION } from '../article-videoproject/data'

import styles from './../articles.module.scss'
import stylesInfo from './article-video-playlist.module.scss'
import stylesVideo from '../article-videoproject/article-videoproject.module.scss'

interface IArticleVideo {
  dataset: IMaterial
  isWebinar?: boolean
  isWithFormat?: boolean
  children?: ReactNode
  playlistData: IMaterialCard[]
}

const ArticleVideoWithPlaylist: FC<IArticleVideo> = (props: IArticleVideo) => {
  const [toggleFeedback, setToggleFeedback] = useState(false)
  const [selectedOption, setSelectedOption] = useState(OPTIONS[0])
  const refPlayer = useRef<any>(null)

  const { dataset, isWebinar, isWithFormat, playlistData, children } = props

  const socket = useContext(statisticContext)
  const isBigPC = usePCWindow(ScreenSize.big)
  const isFullInShow = selectedOption === SWITCH_DESCRIPTION
  const place = isWebinar ? PLACEFLAG_PAST : PLACEFLAG_VIDEO
  const isMustBePaid = dataset?.isAcceptAllow === false

  let currWebinarIndex = 0
  if (playlistData?.length > 0) currWebinarIndex = getCurrVideoIndex(playlistData, dataset.id)
  const isLoaded = playlistData.length > 0

  const handleToggleFeedback = () => setToggleFeedback((prevValue) => !prevValue)
  const handleTimeCode = (time: number) => getPlayerMethods(time, dataset.videoLink, refPlayer)
  const lectorsData = useLectorsSubscription(dataset.Lecturs, place)

  const Info = useMemo(
    () => (
      <>
        <VideoBlockInfo
          dataset={dataset}
          lectorsData={lectorsData}
          isMustBePaid={isMustBePaid}
          isEnded
          handleTimeCode={handleTimeCode}
        />
        {!dataset.isHideComments && (
          <div className={styles.commentariesTop}>
            <Commentaries
              comments={dataset.Comments}
              id={dataset.id}
              materialType={isWebinar ? 'webinar' : 'article'}
              isWithNMO={Boolean(dataset.activeNMOCodes)}
              isAllowComment={dataset?.isAcceptAllow}
            />
          </div>
        )}
      </>
    ),
    [dataset, lectorsData, isMustBePaid]
  )

  useEffect(() => {
    if (isBigPC) setSelectedOption(SWITCH_DESCRIPTION)
  }, [isBigPC])

  useEffect(() => {
    if (isWebinar && socket?.sendEvent) socket?.sendEvent(SocketEvents.WEBINAR_RECORD)
  }, [socket])

  return (
    <div className={stylesVideo.articleVideo}>
      <WriteUs toggleFeedback={toggleFeedback} setToggleFeedback={handleToggleFeedback} />
      <div className={styles.articleWrapper}>
        <div
          className={clsx(stylesVideo.articleTopVideo, {
            [stylesVideo.wrapperWithFormat]: isWithFormat,
          })}
        >
          {isWithFormat && (
            <section
              className={clsx(styles.recomendationSection, styles.recomendationSectionVertical)}
            >
              <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
                <div>{children}</div>
              </WidthLimiter>
            </section>
          )}
          <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
            <div className={stylesVideo.whiteBgVideo}>
              <main>
                <div className={stylesVideo.breadcrumb}>
                  <BreadcrumbsPage
                    breadData={[
                      {
                        name: isWebinar ? 'Вебинары' : 'Библиотека',
                        link: isWebinar ? `${URL_SITE}/webinar` : `${URL_SITE}/library`,
                      },
                      { name: dataset.name, link: '' },
                    ]}
                  />
                </div>
                <VideoFrameMaterial dataset={dataset} refPlayerOuter={refPlayer} />
                {/* <VideoBlockProject {...props} isFullInShow={isFullInShow} /> */}

                <div className={stylesInfo.infoHorizontal}>{Info}</div>

                {!isBigPC && (
                  <div className={styles.toggleWrap}>
                    <SwitchToggle
                      options={OPTIONS}
                      selectedOption={selectedOption}
                      handleToggle={setSelectedOption}
                      modificator={stylesInfo.toggleOption}
                      wrapperModificator={stylesInfo.toggleContent}
                      lineModificator={stylesInfo.line}
                      fadeModificator={stylesInfo.toggleFade}
                      isWithoutFade
                    />
                  </div>
                )}

                {!isFullInShow && (
                  <div className={stylesInfo.playlistWrapMobile}>
                    <Playlist data={playlistData} />
                  </div>
                )}
              </main>
            </div>
            {selectedOption === OPTIONS[0] && <div className={stylesInfo.infoVertical}>{Info}</div>}
          </WidthLimiter>
          {isBigPC && (
            <div className={stylesVideo.playlistWrap}>
              <div className={stylesVideo.playlistHeader}>
                <p className={stylesVideo.playlistTitle}>Следующие видео</p>
                {isLoaded && (
                  <p className={stylesVideo.playlistCount}>{`${currWebinarIndex + 1} из ${
                    playlistData.length
                  } видео`}</p>
                )}
              </div>
              {isLoaded ? (
                <Playlist data={playlistData} modificator={stylesVideo.scrollWrap} />
              ) : (
                <div className={stylesVideo.loader}>
                  <Loader />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* {isFullInShow && (
        <div className={styles.commentariesBottom}>
          <WidthLimiter
            noTopPadding
            noBottomPadding
            noLeftPadding
            noRightPadding
            modificator={styles.widthLimMod}
          >
            {!dataset.isHideComments && (
              <Commentaries
                comments={dataset.Comments}
                id={dataset.id}
                materialType={isWebinar ? 'webinar' : 'article'}
                isWithNMO={Boolean(dataset.activeNMOCodes)}
                isAllowComment={dataset?.isAcceptAllow}
              />
            )}
          </WidthLimiter>
        </div>
      )} */}
    </div>
  )
}

export default ArticleVideoWithPlaylist
